import axios from 'axios';
import logger from '../utils/logger';

// Resolve backend URL with a sensible local default for development
// and guard against accidentally including `/api` in the env variable.
const RAW_BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Normalize: remove trailing slashes
let BACKEND_URL = RAW_BACKEND_URL.replace(/\/+$/, '');

// If someone set REACT_APP_BACKEND_URL to ".../api", strip that segment
if (BACKEND_URL.toLowerCase().endsWith('/api')) {
  BACKEND_URL = BACKEND_URL.slice(0, -4);
}

// Always hit the backend through the `/api` prefix
const API_BASE = `${BACKEND_URL}/api`;

// Default timeout for API calls
const DEFAULT_TIMEOUT = 15000;

// ✅ Create axios instance with credentials enabled (for authenticated endpoints)
export const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // ✅ CRITICAL: Send cookies with every request
});

// ✅ Create public axios instance without credentials (for public endpoints)
export const publicApiClient = axios.create({
  baseURL: API_BASE,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // ✅ Public endpoints don't need cookies
});

// ✅ Add response interceptor for public client (simpler error handling)
publicApiClient.interceptors.response.use(
  (response) => {
    logger.debug(`Public API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    // Handle errors gracefully for public endpoints
    if (!error.response) {
      logger.warn('⚠️ Network error on public endpoint:', error.message);
    } else if (error.response?.status !== 401) {
      // Only log non-401 errors (401 shouldn't happen on public endpoints, but handle gracefully)
      logger.error('Public API Error:', error.response?.status, error.response?.data || error.message);
    }
    return Promise.reject(error);
  }
);

// ✅ Request interceptor with offline detection
apiClient.interceptors.request.use(
  (config) => {
    // ✅ Check if user is offline before making request
    if (!navigator.onLine) {
      logger.warn('⚠️ Network offline - blocking API request:', config.url);
      // Return a rejected promise with a custom offline error
      return Promise.reject({
        isOffline: true,
        message: 'No internet connection',
        config
      });
    }
    
    logger.debug(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    // Only log unexpected request errors
    logger.debug('API Request Error:', error);
    return Promise.reject(error);
  }
);

// ✅ Updated response interceptor with offline handling
apiClient.interceptors.response.use(
  (response) => {
    logger.debug(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    // ✅ Handle offline errors from request interceptor
    if (error.isOffline) {
      logger.log('🔌 API call blocked - device is offline');
      return Promise.reject(error);
    }
    
    // ✅ Suppress 401 errors for verify-token endpoint (expected for unauthenticated users)
    const isVerifyTokenEndpoint = error.config?.url?.includes('/auth/verify-token');
    if (error.response?.status === 401 && isVerifyTokenEndpoint) {
      // Silently handle 401 for verify-token - this is expected for unauthenticated users
      // Prevent axios from logging to console by creating a custom error
      const silentError = new Error('Authentication check: No valid session');
      silentError.response = error.response;
      silentError.config = error.config;
      silentError.isSilent = true; // Flag to prevent console logging
      return Promise.reject(silentError);
    }
    
    // ✅ Handle network errors (no response received)
    if (!error.response) {
      // Handle specific network error types
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const isContentLengthMismatch = error.message?.includes('ERR_CONTENT_LENGTH_MISMATCH') || 
                                     error.code === 'ERR_CONTENT_LENGTH_MISMATCH';
      
      // Log timeout errors at debug level to reduce console noise
      if (isTimeout) {
        logger.debug(`⏱️ Request timeout: ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
      } 
      // Log content length mismatch at debug level - this is often a transient network issue
      else if (isContentLengthMismatch) {
        logger.debug(`⚠️ Content length mismatch (transient network issue): ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
      }
      // Only warn for other network errors
      else {
        logger.warn('⚠️ Network error - no response received:', error.message);
      }
      
      return Promise.reject({
        isNetworkError: true,
        isContentLengthMismatch: isContentLengthMismatch,
        message: error.message || 'Network error',
        originalError: error
      });
    }
    
    if (error.response?.status === 401) {
      // Cookie expired or invalid - this is expected for unauthenticated users
      // Suppress all logging for 401 errors - they're normal for public pages
      logger.debug('Authentication check: No valid session (expected for unauthenticated users)');
      
      // ✅ Prevent axios from logging to console by modifying the error
      // This prevents the browser console from showing 401 errors
      const silentError = new Error('Authentication required');
      silentError.response = error.response;
      silentError.config = error.config;
      silentError.isAxiosError = true;
      silentError.toJSON = error.toJSON;
      // Mark as silent to prevent console logging
      silentError.silent = true;
      return Promise.reject(silentError);
    }
    
    // ✅ Handle validation errors (422)
    if (error.response?.status === 422 && error.response?.data?.detail) {
      logger.error('Validation Error:', error.response.data.detail);
    } else if (error.response?.status !== 401) {
      // Only log non-401 errors (401 is expected for unauthenticated users)
      logger.error('API Response Error:', error.response?.status, error.response?.data || error.message);
    }
    
    return Promise.reject(error);
  }
);

/**
 * API Service Functions
 */

export const industryService = {
  // Get all industries (public endpoint - no authentication required)
  async getAll() {
    try {
      // ✅ Use public API client (no credentials) for public endpoints
      const response = await publicApiClient.get('/industries');
      return {
        success: true,
        data: response.data.industries || [],
        error: null
      };
    } catch (error) {
      // ✅ Handle offline errors gracefully
      if (error.isOffline) {
        return {
          success: false,
          data: [],
          error: 'No internet connection',
          isOffline: true
        };
      }
      
      // ✅ Handle 401 gracefully (expected for unauthenticated users)
      // Don't log as error - this is expected behavior for public pages
      if (error.response?.status === 401) {
        return {
          success: false,
          data: [],
          error: null, // Don't set error message for expected 401s
          isUnauthenticated: true
        };
      }
      
      // Only log non-401 errors
      if (error.response?.status !== 401) {
        logger.error('Failed to fetch industries:', error);
      }
      
      return {
        success: false,
        data: [],
        error: error.message || 'Failed to fetch industries'
      };
    }
  },

  // Get questions for specific industry (legacy method)
  async getQuestions(industryName, region = 'naics') {
    try {
      const encodedIndustry = encodeURIComponent(industryName);
      const response = await apiClient.get(`/questions/${encodedIndustry}?region=${region}`);
      return {
        success: true,
        data: response.data.questions || [],
        error: null
      };
    } catch (error) {
      // ✅ Handle offline errors gracefully
      if (error.isOffline) {
        return {
          success: false,
          data: [],
          error: 'No internet connection',
          isOffline: true
        };
      }
      
      return {
        success: false,
        data: [],
        error: error.message || 'Failed to fetch questions'
      };
    }
  },

  // Get questions for specific sub-industry by ID (new schema)
  async getQuestionsBySubIndustry(subIndustryId) {
    try {
      const response = await publicApiClient.get(`/questions-by-sub-industry/${subIndustryId}`);
      return {
        success: true,
        questions: response.data.questions || [],
        data: response.data.questions || [],
        error: null
      };
    } catch (error) {
      // ✅ Handle offline errors gracefully
      if (error.isOffline) {
        return {
          success: false,
          questions: [],
          data: [],
          error: 'No internet connection',
          isOffline: true
        };
      }
      
      return {
        success: false,
        questions: [],
        data: [],
        error: error.message || 'Failed to fetch questions'
      };
    }
  }
};

export const reportService = {
  // Generate sample report data
  async generateSample(industryName, region = 'naics') {
    try {
      const industrySlug = industryName.toLowerCase().replace(' & ', '-').replace(/\s+/g, '-');
      const response = await apiClient.post(`/reports/sample/${industrySlug}?region=${region}`);
      return {
        success: true,
        data: response.data.sample_data || {},
        error: null
      };
    } catch (error) {
      // ✅ Handle offline errors gracefully
      if (error.isOffline) {
        return {
          success: false,
          data: {},
          error: 'No internet connection',
          isOffline: true
        };
      }
      
      return {
        success: false,
        data: {},
        error: error.message || 'Failed to generate sample report'
      };
    }
  },

  // Get report preview URL
  getPreviewUrl(industryName, region = 'naics') {
    const industrySlug = industryName.toLowerCase().replace(' & ', '-').replace(/\s+/g, '-');
    return `${API_BASE}/reports/sample/${industrySlug}/preview?region=${region}`;
  },

  // Get report download URL
  getDownloadUrl(industryName, region = 'naics') {
    const industrySlug = industryName.toLowerCase().replace(' & ', '-').replace(/\s+/g, '-');
    return `${API_BASE}/reports/sample/${industrySlug}/download?region=${region}`;
  }
};

export const assessmentService = {
  // Submit assessment
  async submit(assessmentData) {
    try {
      const response = await apiClient.post('/assessment/submit', assessmentData);
      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      // ✅ Handle offline errors gracefully
      if (error.isOffline) {
        return {
          success: false,
          data: null,
          error: 'No internet connection. Please try again when online.',
          isOffline: true
        };
      }
      
      logger.error('API Response Error:', error.response?.status, error.response?.data);
      
      // CRITICAL FIX: Return the full error.response.data which contains the detail object
      if (error.response && error.response.data) {
        return {
          success: false,
          data: null,
          error: error.response.data  // This is { detail: { error: 'duplicate_assessment', message: '...' } }
        };
      }
      
      // For network errors or other issues without response
      return {
        success: false,
        data: null,
        error: { message: error.message || 'Failed to submit assessment' }
      };
    }
  },

  // Get user assessments
  async getUserAssessments() {
    try {
      const response = await apiClient.get('/assessments');
      return {
        success: true,
        data: response.data.assessments || [],
        error: null
      };
    } catch (error) {
      // ✅ Handle offline errors gracefully
      if (error.isOffline) {
        return {
          success: false,
          data: [],
          error: 'No internet connection',
          isOffline: true
        };
      }
      
      if (error.response && error.response.data) {
        return {
          success: false,
          data: [],
          error: error.response.data
        };
      }
      
      return {
        success: false,
        data: [],
        error: { message: error.message || 'Failed to fetch assessments' }
      };
    }
  }
};


// ✅ Updated generic API call wrapper with offline handling
export const apiCall = async (method, endpoint, data = null, config = {}) => {
  try {
    const response = await apiClient({
      method,
      url: endpoint,
      data,
      ...config
    });
    
    // If the backend already returns { success, data, ... }, use it directly
    // Otherwise wrap it
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      return response.data;
    }
    
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error) {
    // ✅ Handle offline errors from request interceptor
    if (error.isOffline) {
      logger.debug('API call blocked - device is offline');
      return {
        success: false,
        data: null,
        error: 'No internet connection',
        isOffline: true
      };
    }
    
    // ✅ Handle network errors (no response received)
    if (error.isNetworkError) {
      logger.debug('Network error detected');
      return {
        success: false,
        data: null,
        error: error.message || 'Network error - please check your connection',
        isNetworkError: true
      };
    }
    
    // Only log non-401 errors (401 is expected for unauthenticated users)
    if (error.response?.status !== 401) {
      logger.error('API Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
    }
    
    // ✅ Handle FastAPI validation errors (422)
    if (error.response?.status === 422) {
      const responseData = error.response.data;
      
      // Check if it's our custom validation error format
      if (responseData && responseData.success === false) {
        return {
          success: false,
          data: null,
          error: responseData.message || 'Validation error',
          errors: responseData.errors // Include field-level errors if present
        };
      }
      
      // Handle raw FastAPI validation errors (array format)
      if (responseData?.detail && Array.isArray(responseData.detail)) {
        const errorMessage = responseData.detail.map(err => {
          const field = err.loc ? err.loc[err.loc.length - 1] : 'field';
          const msg = err.msg || 'Invalid value';
          return `${field}: ${msg}`;
        }).join(', ');
        
        return {
          success: false,
          data: null,
          error: errorMessage
        };
      }
    }
    
    // ✅ Handle error responses with our standard format
    // Backend now returns: { success: false, error: "Error Type", message: "User message", data: {...} }
    if (error.response?.data) {
      const responseData = error.response.data;
      
      // ✅ PRIORITY 1: Check for detail field first (FastAPI HTTPException format)
      if (responseData.detail) {
        // If detail is an object with message field, preserve the full object
        if (typeof responseData.detail === 'object' && !Array.isArray(responseData.detail)) {
          // If it has a message field, use it as the error message
          if (responseData.detail.message) {
            return {
              success: false,
              data: null,
              error: responseData.detail.message,
              errorObject: responseData.detail // Preserve full error object
            };
          }
          // If it's an object but no message, use the whole object
          return {
            success: false,
            data: null,
            error: JSON.stringify(responseData.detail),
            errorObject: responseData.detail // Preserve full error object
          };
        }
        // If detail is a string that looks like a Python dict or JSON, try to parse it
        if (typeof responseData.detail === 'string') {
          // Try to parse if it looks like JSON or Python dict
          let parsedDetail = null;
          try {
            // Try JSON first
            parsedDetail = JSON.parse(responseData.detail);
          } catch (e) {
            // If JSON parsing fails, try to parse Python dict format
            // Replace single quotes with double quotes and try again
            try {
              const jsonString = responseData.detail.replace(/'/g, '"');
              parsedDetail = JSON.parse(jsonString);
            } catch (e2) {
              // If both fail, check if it's a string representation of an object
              // Try to extract message using regex if it looks like {'message': '...'}
              const messageMatch = responseData.detail.match(/['"]message['"]\s*:\s*['"]([^'"]+)['"]/);
              if (messageMatch) {
                return {
                  success: false,
                  data: null,
                  error: messageMatch[1], // Extract just the message text
                  errorObject: { message: messageMatch[1] }
                };
              }
            }
          }
          
          // If we successfully parsed it and it has the expected structure
          if (parsedDetail && typeof parsedDetail === 'object' && parsedDetail.message) {
            return {
              success: false,
              data: null,
              error: parsedDetail.message, // Extract just the message text
              errorObject: parsedDetail // Preserve full error object
            };
          }
          
          // If we parsed it but it doesn't have message, try to extract message from error field
          if (parsedDetail && typeof parsedDetail === 'object') {
            return {
              success: false,
              data: null,
              error: parsedDetail.message || parsedDetail.error || responseData.detail,
              errorObject: parsedDetail // Preserve full error object
            };
          }
          
          // Otherwise, use the string as-is but try to extract message if it looks like an object string
          const messageMatch = responseData.detail.match(/['"]message['"]\s*:\s*['"]([^'"]+)['"]/);
          if (messageMatch) {
            return {
              success: false,
              data: null,
              error: messageMatch[1], // Extract just the message text
              errorObject: { message: messageMatch[1] }
            };
          }
          
          return {
            success: false,
            data: null,
            error: responseData.detail,
            errorObject: { message: responseData.detail } // Create error object from string
          };
        }
      }
      
      // ✅ PRIORITY 2: Check for our standard error format
      if (responseData.success === false && responseData.message) {
        // Preserve full error object if it exists
        const errorObject = responseData.error ? {
          error: responseData.error,
          message: responseData.message
        } : { message: responseData.message };
        
        return {
          success: false,
          data: responseData.data || null, // ✅ PRESERVE data field (e.g., consultant_status)
          error: responseData.message, // Use the user-friendly message
          errorType: responseData.error, // Include error type if needed
          errorObject: errorObject // Preserve full error object
        };
      }
      
      // ✅ PRIORITY 3: If response has a message field directly
      if (responseData.message) {
        return {
          success: false,
          data: null,
          error: responseData.message
        };
      }
    }
    
    // Fallback for network errors or other issues
    return {
      success: false,
      data: null,
      error: error.message || `Request failed with status code ${error.response?.status || 'unknown'}`
    };
  }
};

export default {
  industryService,
  reportService,
  assessmentService,
  apiCall
};
